import { formatOrgNumber, type PublicProviderProfile } from '@hemmafixa/shared';
import { Avatar, Badge, Card } from '@hemmafixa/ui';
import { BadgeCheck, Globe, MapPin } from 'lucide-react';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from './catalogue';

/** A verified provider as clients see them (P2-11). The same view is the provider's own preview. */
export function PublicProviderProfileView({ profile }: { profile: PublicProviderProfile }) {
  const { t, date } = useI18n();
  const categoryLabel = useCategoryLabel();
  const p = t.publicProfile;

  return (
    <div className="grid gap-4">
      <Card className="grid gap-3 p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar seed={profile.id} name={profile.companyName} kind="company" size="lg" />
            <div className="grid min-w-0 gap-1">
              <h2 className="text-xl font-semibold">{profile.companyName}</h2>
              <p className="text-sm text-muted-foreground">
                {p.orgNumber} {formatOrgNumber(profile.orgNumber)} ·{' '}
                {fill(p.memberSince, { date: date(profile.memberSince) })}
              </p>
            </div>
          </div>
          <Badge tone="success" className="gap-1" title={p.verifiedHint}>
            <BadgeCheck className="size-3.5" aria-hidden />
            {p.verified}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{p.verifiedHint}</p>
      </Card>

      <Card className="grid gap-2 p-5 sm:p-6">
        <h3 className="font-semibold">{p.about}</h3>
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
          {profile.about ?? p.noAbout}
        </p>
        {profile.website && (
          <a
            href={profile.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1.5 justify-self-start text-sm font-medium underline underline-offset-2"
          >
            <Globe className="size-4" aria-hidden />
            {profile.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </Card>

      <Card className="grid gap-4 p-5 sm:p-6">
        <div className="grid gap-2">
          <h3 className="font-semibold">{p.services}</h3>
          <div className="flex flex-wrap gap-1.5">
            {profile.categorySlugs.map((slug) => (
              <Badge key={slug}>{categoryLabel(slug)}</Badge>
            ))}
          </div>
        </div>
        <div className="grid gap-2">
          <h3 className="font-semibold">{p.areas}</h3>
          <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
            {profile.counties.map((county) => county.name).join(', ')}
          </p>
        </div>
      </Card>

      <p className="text-sm text-muted-foreground">{p.contactNote}</p>
    </div>
  );
}
